<div class="min-vh-100 bg-light">
  <app-header [page]="'Admin'"></app-header>

  <main class="container py-4">
    <section class="house-mode-banner mb-4">
      <div class="d-flex flex-wrap justify-content-between align-items-center gap-3">
        <div>
          <div class="house-mode-eyebrow">HOUSE MANAGEMENT MODE</div>
          <h3 class="mb-1">SwipFx House Liquidity</h3>
          <p class="mb-0 opacity-75">
            Manage house wallets, instant-rate routes, backstop trades, and operational controls.
          </p>
        </div>

        <div class="text-md-end">
          <div class="small opacity-75">Authenticated staff role</div>
          <div class="fw-semibold">{{ actorLabel }}</div>
          <div class="small mt-1">Actions remain attributed to your staff account.</div>
        </div>
      </div>
    </section>

    <div class="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
      <div>
        <h4 class="mb-1">House Operations</h4>
        <p class="text-muted mb-0">
          The workspace does not impersonate the house user. All mutations are authorized and audited against you.
        </p>
      </div>

      <div class="d-flex flex-wrap gap-2">
        <a *ngIf="canMarketMaker" class="btn btn-primary" routerLink="/admin/house/market-maker">Open Market Maker</a>
        <button class="btn btn-outline-primary" type="button" (click)="refreshCurrentTab()">
          Refresh current view
        </button>
      </div>
    </div>

    <div class="house-tabs mb-4" role="tablist" aria-label="House management sections">
      <button
        *ngFor="let tab of tabs"
        type="button"
        class="house-tab"
        [class.active]="activeTab === tab.key"
        (click)="setTab(tab.key)">
        {{ tab.label }}
        <span
          *ngIf="tab.key === 'operations' && overview?.openLiquidityAlertCount"
          class="badge rounded-pill text-bg-danger ms-1">
          {{ overview?.openLiquidityAlertCount }}
        </span>
      </button>
    </div>

    <!-- OVERVIEW -->
    <ng-container *ngIf="activeTab === 'overview'">
      <div *ngIf="overviewLoading" class="house-loading">
        <div class="spinner-border text-primary"></div>
      </div>

      <ng-container *ngIf="!overviewLoading && overview as data">
        <div class="row g-3 mb-4">
          <div class="col-6 col-lg-3">
            <div class="house-card metric-card h-100">
              <div class="metric-label">Available wallets</div>
              <div class="metric-value">{{ data.walletCount }}</div>
              <div class="metric-note">House currency accounts</div>
            </div>
          </div>

          <div class="col-6 col-lg-3">
            <div class="house-card metric-card h-100">
              <div class="metric-label">Active routes</div>
              <div class="metric-value">{{ data.activeCurrencyPairCount }}</div>
              <div class="metric-note">Instant directional pairs</div>
            </div>
          </div>

          <div class="col-6 col-lg-3">
            <div class="house-card metric-card h-100">
              <div class="metric-label">Processing trades</div>
              <div class="metric-value">{{ data.processingInstantTradeCount }}</div>
              <div class="metric-note">Instant executions in progress</div>
            </div>
          </div>

          <div class="col-6 col-lg-3">
            <div class="house-card metric-card h-100" [class.metric-danger]="data.criticalLiquidityAlertCount > 0">
              <div class="metric-label">Liquidity alerts</div>
              <div class="metric-value">{{ data.openLiquidityAlertCount }}</div>
              <div class="metric-note">{{ data.criticalLiquidityAlertCount }} critical</div>
            </div>
          </div>
        </div>

        <div class="row g-3 mb-4">
          <div class="col-lg-8">
            <div class="house-card h-100">
              <div class="house-card-header">
                <div>
                  <h5 class="mb-1">House wallet position</h5>
                  <p class="text-muted small mb-0">Ledger balance, active holds, and immediately available liquidity.</p>
                </div>
                <button class="btn btn-sm btn-outline-primary" type="button" (click)="setTab('liquidity')">
                  Manage liquidity
                </button>
              </div>

              <div class="table-responsive">
                <table class="table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Currency</th>
                      <th>Status</th>
                      <th class="text-end">Ledger</th>
                      <th class="text-end">Active holds</th>
                      <th class="text-end">Available</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *ngFor="let wallet of data.wallets">
                      <td>
                        <div class="fw-semibold">{{ wallet.currency.code }}</div>
                        <div class="text-muted small">{{ wallet.currency.name }}</div>
                      </td>
                      <td><span class="badge" [ngClass]="statusClass(wallet.status)">{{ wallet.status }}</span></td>
                      <td class="text-end">{{ formatAmount(wallet.ledgerBalance, wallet.currency.symbol) }}</td>
                      <td class="text-end">{{ formatAmount(wallet.activeHolds, wallet.currency.symbol) }}</td>
                      <td class="text-end fw-semibold">{{ formatAmount(wallet.availableBalance, wallet.currency.symbol) }}</td>
                    </tr>
                    <tr *ngIf="!data.wallets.length">
                      <td colspan="5" class="text-center text-muted py-4">No house wallets are available.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div class="col-lg-4">
            <div class="house-card h-100">
              <h5 class="mb-3">Operational health</h5>

              <div class="health-row">
                <span>Open house orders</span>
                <strong>{{ data.openHouseOrderCount }}</strong>
              </div>
              <div class="health-row">
                <span>Processing withdrawals</span>
                <strong>{{ data.processingWithdrawalCount }}</strong>
              </div>
              <div class="health-row">
                <span>Last reconciliation</span>
                <span class="badge" [ngClass]="statusClass(data.lastReconciliationStatus)">
                  {{ data.lastReconciliationStatus || 'Not run' }}
                </span>
              </div>
              <div class="health-row border-0">
                <span>Reconciled at</span>
                <strong class="text-end">{{ formatDate(data.lastReconciliationAt) }}</strong>
              </div>

              <button class="btn btn-primary w-100 mt-3" type="button" (click)="setTab('operations')">
                Review operational controls
              </button>
            </div>
          </div>
        </div>
      </ng-container>
    </ng-container>

    <!-- LIQUIDITY -->
    <ng-container *ngIf="activeTab === 'liquidity'">
      <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h5 class="mb-1">House liquidity</h5>
          <p class="text-muted mb-0">Credit external funding into house wallets and review immutable ledger history.</p>
        </div>
        <button class="btn btn-primary" type="button" (click)="openFunding()">Credit house wallet</button>
      </div>

      <div *ngIf="fundingFormVisible" class="house-card form-card mb-4">
        <div class="house-card-header">
          <div>
            <h5 class="mb-1">Credit house liquidity</h5>
            <p class="text-muted small mb-0">Negative adjustments are not permitted. Outgoing funds must use House Withdrawals.</p>
          </div>
          <button class="btn-close" type="button" aria-label="Close" (click)="closeFunding()"></button>
        </div>

        <div class="row g-3">
          <div class="col-md-4">
            <label class="form-label">Wallet</label>
            <select class="form-select" [(ngModel)]="fundingForm.walletId">
              <option value="">Select wallet</option>
              <option *ngFor="let wallet of wallets" [value]="wallet.walletId">
                {{ wallet.currency.code }} — {{ formatAmount(wallet.availableBalance, wallet.currency.symbol) }} available
              </option>
            </select>
          </div>

          <div class="col-md-4">
            <label class="form-label">Adjustment type</label>
            <select class="form-select" [(ngModel)]="fundingForm.type">
              <option value="Funding">Funding</option>
              <option value="CreditCorrection">Credit correction</option>
            </select>
          </div>

          <div class="col-md-4">
            <label class="form-label">Amount</label>
            <input type="number" min="0" step="0.01" class="form-control" [(ngModel)]="fundingForm.amount">
          </div>

          <div class="col-md-6">
            <label class="form-label">External reference</label>
            <input type="text" class="form-control" [(ngModel)]="fundingForm.externalReference"
              [placeholder]="fundingExternalReferencePlaceholder">
            <div class="form-text">Leave blank to generate this reference automatically.</div>
          </div>

          <div class="col-md-6">
            <label class="form-label">Reason</label>
            <input type="text" class="form-control" [(ngModel)]="fundingForm.reason"
              [placeholder]="fundingReasonPlaceholder">
            <div class="form-text">Leave blank to use the suggested reason.</div>
          </div>
        </div>

        <div class="d-flex justify-content-end gap-2 mt-3">
          <button class="btn btn-light border" type="button" (click)="closeFunding()">Cancel</button>
          <button class="btn btn-primary" type="button" [disabled]="savingFunding" (click)="submitFunding()">
            <span *ngIf="savingFunding" class="spinner-border spinner-border-sm me-1"></span>
            Credit wallet
          </button>
        </div>
      </div>

      <div class="house-card mb-4">
        <div class="house-card-header">
          <div>
            <h5 class="mb-1">Wallets</h5>
            <p class="text-muted small mb-0">Available balance already excludes active holds.</p>
          </div>
        </div>

        <div *ngIf="walletsLoading" class="house-loading"><div class="spinner-border text-primary"></div></div>

        <div class="table-responsive" *ngIf="!walletsLoading">
          <table class="table align-middle mb-0">
            <thead>
              <tr>
                <th>Currency</th>
                <th>Status</th>
                <th class="text-end">Ledger balance</th>
                <th class="text-end">Active holds</th>
                <th class="text-end">Available balance</th>
                <th class="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let wallet of wallets">
                <td>
                  <div class="fw-semibold">{{ wallet.currency.code }}</div>
                  <div class="text-muted small">{{ wallet.currency.name }}</div>
                </td>
                <td><span class="badge" [ngClass]="statusClass(wallet.status)">{{ wallet.status }}</span></td>
                <td class="text-end">{{ formatAmount(wallet.ledgerBalance, wallet.currency.symbol) }}</td>
                <td class="text-end">{{ formatAmount(wallet.activeHolds, wallet.currency.symbol) }}</td>
                <td class="text-end fw-semibold">{{ formatAmount(wallet.availableBalance, wallet.currency.symbol) }}</td>
                <td class="text-end">
                  <div class="btn-group btn-group-sm">
                    <button class="btn btn-outline-secondary" type="button" (click)="viewLedger(wallet)">Ledger</button>
                    <button class="btn btn-outline-primary" type="button" (click)="openFunding(wallet)">Credit</button>
                  </div>
                </td>
              </tr>
              <tr *ngIf="!wallets.length">
                <td colspan="6" class="text-center text-muted py-4">No house wallets found.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div *ngIf="selectedLedgerWallet as ledgerWallet" class="house-card mb-4">
        <div class="house-card-header">
          <div>
            <h5 class="mb-1">{{ ledgerWallet.currency.code }} wallet ledger</h5>
            <p class="text-muted small mb-0">Latest immutable postings for the selected wallet.</p>
          </div>
          <button class="btn-close" type="button" aria-label="Close" (click)="closeLedger()"></button>
        </div>

        <div *ngIf="ledgerLoading" class="house-loading"><div class="spinner-border text-primary"></div></div>

        <div class="table-responsive" *ngIf="!ledgerLoading">
          <table class="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Description</th>
                <th>Reference</th>
                <th class="text-end">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let entry of ledgerEntries">
                <td>{{ formatDate(entry.createdAt) }}</td>
                <td>{{ entry.type }}</td>
                <td>{{ entry.description }}</td>
                <td class="text-break"><code>{{ entry.transactionRef || '—' }}</code></td>
                <td class="text-end fw-semibold" [class.text-success]="entry.signedAmount > 0" [class.text-danger]="entry.signedAmount < 0">
                  {{ formatAmount(entry.signedAmount, ledgerWallet.currency.symbol) }}
                </td>
              </tr>
              <tr *ngIf="!ledgerEntries.length">
                <td colspan="5" class="text-center text-muted py-4">No ledger entries found.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="table-pager" *ngIf="ledgerMeta">
          <span>Page {{ ledgerMeta.currentPage }} of {{ ledgerMeta.totalPages || 1 }}</span>
          <div class="btn-group btn-group-sm">
            <button class="btn btn-outline-secondary" [disabled]="ledgerMeta.currentPage <= 1"
              (click)="viewLedger(ledgerWallet, ledgerMeta.currentPage - 1)">Previous</button>
            <button class="btn btn-outline-secondary" [disabled]="ledgerMeta.currentPage >= ledgerMeta.totalPages"
              (click)="viewLedger(ledgerWallet, ledgerMeta.currentPage + 1)">Next</button>
          </div>
        </div>
      </div>

      <div class="house-card">
        <div class="house-card-header">
          <div>
            <h5 class="mb-1">Liquidity funding history</h5>
            <p class="text-muted small mb-0">Every credit records the real staff actor and an idempotent external reference.</p>
          </div>
        </div>

        <div *ngIf="adjustmentsLoading" class="house-loading"><div class="spinner-border text-primary"></div></div>

        <div class="table-responsive" *ngIf="!adjustmentsLoading">
          <table class="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th>Date</th>
                <th>Currency</th>
                <th>Type</th>
                <th>External reference</th>
                <th>Reason</th>
                <th class="text-end">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let item of adjustments">
                <td>{{ formatDate(item.createdAt) }}</td>
                <td>{{ item.currency }}</td>
                <td>{{ item.type }}</td>
                <td><code>{{ item.externalReference }}</code></td>
                <td>{{ item.reason }}</td>
                <td class="text-end fw-semibold">{{ formatAmount(item.amount, getWalletSymbol(item.walletId)) }}</td>
              </tr>
              <tr *ngIf="!adjustments.length">
                <td colspan="6" class="text-center text-muted py-4">No liquidity credits found.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="table-pager" *ngIf="adjustmentsMeta">
          <span>Page {{ adjustmentsMeta.currentPage }} of {{ adjustmentsMeta.totalPages || 1 }}</span>
          <div class="btn-group btn-group-sm">
            <button class="btn btn-outline-secondary" [disabled]="adjustmentsMeta.currentPage <= 1"
              (click)="loadAdjustments(adjustmentsMeta.currentPage - 1)">Previous</button>
            <button class="btn btn-outline-secondary" [disabled]="adjustmentsMeta.currentPage >= adjustmentsMeta.totalPages"
              (click)="loadAdjustments(adjustmentsMeta.currentPage + 1)">Next</button>
          </div>
        </div>
      </div>
    </ng-container>

    <!-- CURRENCY PAIRS -->
    <ng-container *ngIf="activeTab === 'pairs'">
      <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h5 class="mb-1">Directional instant-trade routes</h5>
          <p class="text-muted mb-0">Configure rates independently for each direction, including manual and provider-backed modes.</p>
        </div>
        <button class="btn btn-primary" type="button" (click)="openCreatePair()">Add currency pair</button>
      </div>

      <div *ngIf="pairFormVisible" class="house-card form-card mb-4">
        <div class="house-card-header">
          <div>
            <h5 class="mb-1">{{ pairForm.id ? 'Edit' : 'Create' }} directional pair</h5>
            <p class="text-muted small mb-0">The reverse direction is a separate route with independent markup and liquidity limits.</p>
          </div>
          <button class="btn-close" type="button" aria-label="Close" (click)="closePairForm()"></button>
        </div>

        <div class="row g-3">
          <div class="col-md-3">
            <label class="form-label">From currency</label>
            <select class="form-select" [(ngModel)]="pairForm.fromCurrency"
              (ngModelChange)="onPairFromCurrencyChanged()"
              [disabled]="!!pairForm.id || currenciesLoading">
              <option value="">{{ currenciesLoading ? 'Loading currencies...' : 'Select currency' }}</option>
              <option *ngFor="let currency of pairFromCurrencyOptions" [value]="currency.code">
                {{ currency.code }} — {{ currency.name }}
              </option>
            </select>
          </div>

          <div class="col-md-3">
            <label class="form-label">To currency</label>
            <select class="form-select" [(ngModel)]="pairForm.toCurrency"
              (ngModelChange)="onPairToCurrencyChanged()"
              [disabled]="!!pairForm.id || currenciesLoading">
              <option value="">{{ currenciesLoading ? 'Loading currencies...' : 'Select currency' }}</option>
              <option *ngFor="let currency of pairToCurrencyOptions" [value]="currency.code">
                {{ currency.code }} — {{ currency.name }}
              </option>
            </select>
          </div>

          <div class="col-md-3">
            <label class="form-label">Markup (basis points)</label>
            <input type="number" min="0" step="0.01" class="form-control" [(ngModel)]="pairForm.markupBasisPoints">
            <div class="form-text">{{ basisPointsToPercent(pairForm.markupBasisPoints) }} speed markup</div>
          </div>

          <div class="col-md-3" *ngIf="!pairForm.id">
            <label class="form-label">Initial status</label>
            <select class="form-select" [(ngModel)]="pairForm.status">
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>

          <div class="col-md-3">
            <label class="form-label">Minimum instant amount</label>
            <input type="number" min="0" step="0.01" class="form-control" [(ngModel)]="pairForm.minimumInstantAmount">
          </div>

          <div class="col-md-3">
            <label class="form-label">Maximum instant amount</label>
            <input type="number" min="0" step="0.01" class="form-control" [(ngModel)]="pairForm.maximumInstantAmount">
          </div>

          <div class="col-md-3">
            <label class="form-label">House liquidity buffer</label>
            <input type="number" min="0" step="0.01" class="form-control" [(ngModel)]="pairForm.minimumHouseLiquidityBuffer">
          </div>

          <div class="col-md-3">
            <label class="form-label">Quote lifetime (seconds)</label>
            <input type="number" min="5" max="300" class="form-control" [(ngModel)]="pairForm.quoteTtlSeconds">
          </div>

          <div class="col-md-4">
            <label class="form-label">Reference-rate source</label>
            <select class="form-select" [(ngModel)]="pairForm.rateProviderCode" (change)="onRateProviderChanged()">
              <option value="MANUAL">Manual rate</option>
              <option *ngFor="let provider of rateProviders" [value]="provider.code" [hidden]="provider.isManual">
                {{ provider.name }} ({{ provider.code }})
              </option>
            </select>
          </div>

          <div class="col-md-4" *ngIf="!pairUsesManualRate">
            <label class="form-label d-block">Provider fallback</label>
            <div class="form-check form-switch mt-2">
              <input class="form-check-input" type="checkbox" role="switch" id="manualFallback"
                [(ngModel)]="pairForm.allowManualFallback">
              <label class="form-check-label" for="manualFallback">Use manual rate when provider is unavailable</label>
            </div>
          </div>

          <div class="col-md-4" *ngIf="pairUsesManualRate || pairAllowsFallback">
            <label class="form-label">Manual reference rate</label>
            <input type="number" min="0" step="0.000000000001" class="form-control"
              [(ngModel)]="pairForm.manualReferenceRate">
            <div class="form-text">Units of {{ pairForm.toCurrency || 'target currency' }} per 1 {{ pairForm.fromCurrency || 'source currency' }}.</div>
          </div>
        </div>

        <div class="d-flex justify-content-end gap-2 mt-3">
          <button class="btn btn-light border" type="button" (click)="closePairForm()">Cancel</button>
          <button class="btn btn-primary" type="button" [disabled]="savingPair" (click)="savePair()">
            <span *ngIf="savingPair" class="spinner-border spinner-border-sm me-1"></span>
            Save pair
          </button>
        </div>
      </div>

      <div class="house-card filter-card mb-3">
        <div class="row g-3 align-items-end">
          <div class="col-md-3">
            <label class="form-label">From currency</label>
            <select class="form-select" [(ngModel)]="pairFilters.fromCurrency"
              (ngModelChange)="onPairFilterFromCurrencyChanged()" [disabled]="currenciesLoading">
              <option value="">All source currencies</option>
              <option *ngFor="let currency of pairFilterFromCurrencyOptions" [value]="currency.code">
                {{ currency.code }} — {{ currency.name }}
              </option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label">To currency</label>
            <select class="form-select" [(ngModel)]="pairFilters.toCurrency"
              (ngModelChange)="onPairFilterToCurrencyChanged()" [disabled]="currenciesLoading">
              <option value="">All target currencies</option>
              <option *ngFor="let currency of pairFilterToCurrencyOptions" [value]="currency.code">
                {{ currency.code }} — {{ currency.name }}
              </option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label">Status</label>
            <select class="form-select" [(ngModel)]="pairFilters.status">
              <option value="">All statuses</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
          <div class="col-md-3 d-flex gap-2">
            <button class="btn btn-primary flex-grow-1" type="button" (click)="loadPairs()">Apply</button>
            <button class="btn btn-light border" type="button" (click)="resetPairFilters()">Reset</button>
          </div>
        </div>
      </div>

      <div class="house-card">
        <div *ngIf="pairsLoading" class="house-loading"><div class="spinner-border text-primary"></div></div>
        <div class="table-responsive" *ngIf="!pairsLoading">
          <table class="table align-middle mb-0">
            <thead>
              <tr>
                <th>Pair</th>
                <th>Rate source</th>
                <th class="text-end">Reference rate</th>
                <th class="text-end">Markup</th>
                <th class="text-end">Instant range</th>
                <th class="text-end">Target buffer</th>
                <th>Status</th>
                <th class="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let pair of pairs">
                <td>
                  <div class="fw-semibold">{{ pair.fromCurrency }} → {{ pair.toCurrency }}</div>
                  <div class="text-muted small">Rate validity: {{ pair.quoteTtlSeconds }}s</div>
                </td>
                <td>
                  <div>{{ pair.rateSourceMode }}</div>
                  <div class="text-muted small">{{ pair.rateProviderCode || 'MANUAL' }}</div>
                </td>
                <td class="text-end">{{ pair.manualReferenceRate || '—' }}</td>
                <td class="text-end">{{ basisPointsToPercent(pair.markupBasisPoints) }}</td>
                <td class="text-end">
                  {{ pair.minimumInstantAmount ?? '—' }} – {{ pair.maximumInstantAmount ?? '—' }} {{ pair.fromCurrency }}
                </td>
                <td class="text-end">{{ formatAmount(pair.minimumHouseLiquidityBuffer) }} {{ pair.toCurrency }}</td>
                <td><span class="badge" [ngClass]="statusClass(pair.status)">{{ pair.status }}</span></td>
                <td class="text-end">
                  <div class="btn-group btn-group-sm">
                    <button class="btn btn-outline-secondary" type="button" (click)="editPair(pair)">Edit</button>
                    <button class="btn" type="button"
                      [ngClass]="pair.status === 'Active' ? 'btn-outline-warning' : 'btn-outline-success'"
                      (click)="togglePairStatus(pair)">
                      {{ pair.status === 'Active' ? 'Disable' : 'Enable' }}
                    </button>
                  </div>
                </td>
              </tr>
              <tr *ngIf="!pairs.length">
                <td colspan="8" class="text-center text-muted py-4">No directional currency pairs found.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </ng-container>

    <!-- TRADES -->
    <ng-container *ngIf="activeTab === 'trades'">
      <div class="mb-3">
        <h5 class="mb-1">Instant trade execution</h5>
        <p class="text-muted mb-0">Review how much was filled by user liquidity and how much required the house backstop.</p>
      </div>

      <div class="house-card filter-card mb-3">
        <div class="row g-3 align-items-end">
          <div class="col-md-4">
            <label class="form-label">Status</label>
            <select class="form-select" [(ngModel)]="tradeFilters.status">
              <option value="">All statuses</option>
              <option value="Processing">Processing</option>
              <option value="Matched">Matched</option>
              <option value="PartiallyMatched">Partially matched</option>
              <option value="Failed">Failed</option>
            </select>
          </div>
          <div class="col-md-4">
            <label class="form-label">Currency</label>
            <input type="text" maxlength="3" class="form-control" [(ngModel)]="tradeFilters.currency" placeholder="CAD">
          </div>
          <div class="col-md-4 d-flex gap-2">
            <button class="btn btn-primary flex-grow-1" type="button" (click)="loadTrades(1)">Apply</button>
            <button class="btn btn-light border" type="button" (click)="resetTradeFilters()">Reset</button>
          </div>
        </div>
      </div>

      <div class="house-card">
        <div *ngIf="tradesLoading" class="house-loading"><div class="spinner-border text-primary"></div></div>
        <div class="table-responsive" *ngIf="!tradesLoading">
          <table class="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th>Created</th>
                <th>Pair</th>
                <th class="text-end">Quoted send</th>
                <th class="text-end">Quoted receive</th>
                <th class="text-end">User liquidity</th>
                <th class="text-end">House liquidity</th>
                <th class="text-end">Executed</th>
                <th>Status</th>
                <th>Order</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let trade of trades">
                <td>{{ formatDate(trade.createdAt) }}</td>
                <td>{{ trade.fromCurrency }} → {{ trade.toCurrency }}</td>
                <td class="text-end">{{ formatAmount(trade.quotedFromAmount) }} {{ trade.fromCurrency }}</td>
                <td class="text-end">{{ formatAmount(trade.quotedReceiveAmount) }} {{ trade.toCurrency }}</td>
                <td class="text-end">{{ formatAmount(trade.userLiquidityFromAmount) }} {{ trade.fromCurrency }}</td>
                <td class="text-end fw-semibold">{{ formatAmount(trade.houseLiquidityFromAmount) }} {{ trade.fromCurrency }}</td>
                <td class="text-end">{{ formatAmount(trade.executedFromAmount) }} {{ trade.fromCurrency }}</td>
                <td>
                  <span class="badge" [ngClass]="statusClass(trade.status)">{{ trade.status }}</span>
                  <div *ngIf="trade.failureMessage" class="text-danger small mt-1">{{ trade.failureMessage }}</div>
                </td>
                <td><a [routerLink]="['/admin/orders/manage', trade.customerOrderId]">View</a></td>
              </tr>
              <tr *ngIf="!trades.length">
                <td colspan="9" class="text-center text-muted py-4">No instant trades found.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="table-pager" *ngIf="tradesMeta">
          <span>Page {{ tradesMeta.currentPage }} of {{ tradesMeta.totalPages || 1 }}</span>
          <div class="btn-group btn-group-sm">
            <button class="btn btn-outline-secondary" [disabled]="tradesMeta.currentPage <= 1"
              (click)="loadTrades(tradesMeta.currentPage - 1)">Previous</button>
            <button class="btn btn-outline-secondary" [disabled]="tradesMeta.currentPage >= tradesMeta.totalPages"
              (click)="loadTrades(tradesMeta.currentPage + 1)">Next</button>
          </div>
        </div>
      </div>
    </ng-container>

    <!-- ORDERS -->
    <ng-container *ngIf="activeTab === 'orders'">
      <div class="mb-3">
        <h5 class="mb-1">House inverse orders</h5>
        <p class="text-muted mb-0">Targeted house orders are created only as the backstop for the corresponding instant customer order.</p>
      </div>

      <div class="house-card filter-card mb-3">
        <div class="row g-3 align-items-end">
          <div class="col-md-4">
            <label class="form-label">Status</label>
            <select class="form-select" [(ngModel)]="orderFilters.status">
              <option value="">All statuses</option>
              <option value="Reserved">Reserved</option>
              <option value="Open">Open</option>
              <option value="Partial">Partial</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
              <option value="Failed">Failed</option>
            </select>
          </div>
          <div class="col-md-4">
            <label class="form-label">Currency</label>
            <input type="text" maxlength="3" class="form-control" [(ngModel)]="orderFilters.currency" placeholder="NGN">
          </div>
          <div class="col-md-4 d-flex gap-2">
            <button class="btn btn-primary flex-grow-1" type="button" (click)="loadOrders(1)">Apply</button>
            <button class="btn btn-light border" type="button" (click)="resetOrderFilters()">Reset</button>
          </div>
        </div>
      </div>

      <div class="house-card">
        <div *ngIf="ordersLoading" class="house-loading"><div class="spinner-border text-primary"></div></div>
        <div class="table-responsive" *ngIf="!ordersLoading">
          <table class="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th>Created</th>
                <th>Pair</th>
                <th class="text-end">Original amount</th>
                <th class="text-end">Remaining</th>
                <th class="text-end">Rate</th>
                <th>Status</th>
                <th>Reserved for</th>
                <th class="text-end">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let order of orders">
                <td>{{ formatDate(order.createdAt) }}</td>
                <td>{{ order.fromCurrency }} → {{ order.toCurrency }}</td>
                <td class="text-end">{{ formatAmount(order.originalAmount) }} {{ order.fromCurrency }}</td>
                <td class="text-end">{{ formatAmount(order.remainingAmount) }} {{ order.fromCurrency }}</td>
                <td class="text-end">{{ order.rate }}</td>
                <td><span class="badge" [ngClass]="statusClass(order.status)">{{ order.status }}</span></td>
                <td class="text-break"><code>{{ order.reservedForOrderId || '—' }}</code></td>
                <td class="text-end"><a class="btn btn-sm btn-outline-secondary" [routerLink]="['/admin/orders/manage', order.id]">View</a></td>
              </tr>
              <tr *ngIf="!orders.length">
                <td colspan="8" class="text-center text-muted py-4">No house orders found.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="table-pager" *ngIf="ordersMeta">
          <span>Page {{ ordersMeta.currentPage }} of {{ ordersMeta.totalPages || 1 }}</span>
          <div class="btn-group btn-group-sm">
            <button class="btn btn-outline-secondary" [disabled]="ordersMeta.currentPage <= 1"
              (click)="loadOrders(ordersMeta.currentPage - 1)">Previous</button>
            <button class="btn btn-outline-secondary" [disabled]="ordersMeta.currentPage >= ordersMeta.totalPages"
              (click)="loadOrders(ordersMeta.currentPage + 1)">Next</button>
          </div>
        </div>
      </div>
    </ng-container>

    <!-- OPERATIONS -->
    <ng-container *ngIf="activeTab === 'operations'">
      <div class="row g-3">
        <div class="col-xl-7">
          <div class="house-card h-100">
            <div class="house-card-header">
              <div>
                <h5 class="mb-1">Liquidity alerts</h5>
                <p class="text-muted small mb-0">Warnings and critical conditions are generated from configured pair buffers.</p>
              </div>
            </div>

            <div class="row g-2 mb-3">
              <div class="col-md-4">
                <select class="form-select form-select-sm" [(ngModel)]="alertFilters.status">
                  <option value="">All statuses</option>
                  <option value="Open">Open</option>
                  <option value="Acknowledged">Acknowledged</option>
                  <option value="Resolved">Resolved</option>
                </select>
              </div>
              <div class="col-md-3">
                <select class="form-select form-select-sm" [(ngModel)]="alertFilters.severity">
                  <option value="">All severities</option>
                  <option value="Warning">Warning</option>
                  <option value="Critical">Critical</option>
                </select>
              </div>
              <div class="col-md-2">
                <input type="text" maxlength="3" class="form-control form-control-sm" [(ngModel)]="alertFilters.currency" placeholder="CAD">
              </div>
              <div class="col-md-3 d-flex gap-1">
                <button class="btn btn-sm btn-primary flex-grow-1" (click)="loadAlerts(1)">Apply</button>
                <button class="btn btn-sm btn-light border" (click)="resetAlertFilters()">Reset</button>
              </div>
            </div>

            <div *ngIf="alertsLoading" class="house-loading"><div class="spinner-border text-primary"></div></div>

            <div class="alert-list" *ngIf="!alertsLoading">
              <article *ngFor="let alert of alerts" class="alert-item" [ngClass]="alert.severity === 'Critical' ? 'alert-critical' : 'alert-warning'">
                <div class="d-flex justify-content-between gap-3">
                  <div>
                    <div class="d-flex align-items-center gap-2 mb-1">
                      <span class="badge" [ngClass]="statusClass(alert.severity)">{{ alert.severity }}</span>
                      <strong>{{ alert.currency }} liquidity</strong>
                      <span class="badge" [ngClass]="statusClass(alert.status)">{{ alert.status }}</span>
                    </div>
                    <div>{{ alert.message }}</div>
                    <div class="text-muted small mt-1">
                      Route {{ alert.fromCurrency }} → {{ alert.toCurrency }} · Observed {{ formatDate(alert.lastObservedAt) }}
                    </div>
                  </div>
                  <button *ngIf="alert.status === 'Open'" class="btn btn-sm btn-outline-primary align-self-start"
                    type="button" (click)="openAcknowledgeAlert(alert)">Acknowledge</button>
                </div>

                <div class="row g-2 mt-2 small">
                  <div class="col-4"><span class="text-muted">Available:</span> {{ formatAmount(alert.availableBalance) }} {{ alert.currency }}</div>
                  <div class="col-4"><span class="text-muted">Buffer:</span> {{ formatAmount(alert.minimumLiquidityBuffer) }} {{ alert.currency }}</div>
                  <div class="col-4"><span class="text-muted">Holds:</span> {{ formatAmount(alert.activeHolds) }} {{ alert.currency }}</div>
                </div>
              </article>

              <div *ngIf="!alerts.length" class="text-center text-muted py-4">No liquidity alerts found.</div>
            </div>

            <div class="table-pager" *ngIf="alertsMeta">
              <span>Page {{ alertsMeta.currentPage }} of {{ alertsMeta.totalPages || 1 }}</span>
              <div class="btn-group btn-group-sm">
                <button class="btn btn-outline-secondary" [disabled]="alertsMeta.currentPage <= 1"
                  (click)="loadAlerts(alertsMeta.currentPage - 1)">Previous</button>
                <button class="btn btn-outline-secondary" [disabled]="alertsMeta.currentPage >= alertsMeta.totalPages"
                  (click)="loadAlerts(alertsMeta.currentPage + 1)">Next</button>
              </div>
            </div>
          </div>
        </div>

        <div class="col-xl-5">
          <div class="house-card h-100">
            <div class="house-card-header">
              <div>
                <h5 class="mb-1">Reconciliation</h5>
                <p class="text-muted small mb-0">Validate wallets, holds, targeted orders, quotes, and instant trades.</p>
              </div>
              <button class="btn btn-sm btn-primary" type="button" [disabled]="runningReconciliation" (click)="runReconciliation()">
                <span *ngIf="runningReconciliation" class="spinner-border spinner-border-sm me-1"></span>
                Run now
              </button>
            </div>

            <div class="d-flex gap-2 mb-3">
              <select class="form-select form-select-sm" [(ngModel)]="reconciliationStatus">
                <option value="">All statuses</option>
                <option value="Completed">Completed</option>
                <option value="CompletedWithIssues">Completed with issues</option>
                <option value="Failed">Failed</option>
              </select>
              <button class="btn btn-sm btn-outline-secondary" (click)="loadReconciliationRuns(1)">Apply</button>
            </div>

            <div *ngIf="reconciliationLoading" class="house-loading"><div class="spinner-border text-primary"></div></div>

            <div class="reconciliation-list" *ngIf="!reconciliationLoading">
              <button *ngFor="let run of reconciliationRuns" type="button" class="reconciliation-item"
                (click)="viewReconciliationRun(run)">
                <span>
                  <span class="badge me-2" [ngClass]="statusClass(run.status)">{{ run.status }}</span>
                  {{ formatDate(run.startedAt) }}
                </span>
                <strong>{{ run.issueCount }} issue(s)</strong>
              </button>
              <div *ngIf="!reconciliationRuns.length" class="text-center text-muted py-4">No reconciliation runs found.</div>
            </div>

            <div class="table-pager" *ngIf="reconciliationMeta">
              <span>Page {{ reconciliationMeta.currentPage }} of {{ reconciliationMeta.totalPages || 1 }}</span>
              <div class="btn-group btn-group-sm">
                <button class="btn btn-outline-secondary" [disabled]="reconciliationMeta.currentPage <= 1"
                  (click)="loadReconciliationRuns(reconciliationMeta.currentPage - 1)">Previous</button>
                <button class="btn btn-outline-secondary" [disabled]="reconciliationMeta.currentPage >= reconciliationMeta.totalPages"
                  (click)="loadReconciliationRuns(reconciliationMeta.currentPage + 1)">Next</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div *ngIf="selectedAlert" class="house-card form-card mt-3">
        <div class="house-card-header">
          <div>
            <h5 class="mb-1">Acknowledge {{ selectedAlert.currency }} alert</h5>
            <p class="text-muted small mb-0">Acknowledgement records your staff identity but does not resolve the liquidity condition.</p>
          </div>
          <button class="btn-close" type="button" aria-label="Close" (click)="closeAcknowledgeAlert()"></button>
        </div>
        <label class="form-label">Operational note</label>
        <textarea class="form-control" rows="3" [(ngModel)]="acknowledgementNote"
          placeholder="Treasury funding is in progress."></textarea>
        <div class="d-flex justify-content-end gap-2 mt-3">
          <button class="btn btn-light border" (click)="closeAcknowledgeAlert()">Cancel</button>
          <button class="btn btn-primary" [disabled]="acknowledgingAlert" (click)="acknowledgeAlert()">
            <span *ngIf="acknowledgingAlert" class="spinner-border spinner-border-sm me-1"></span>
            Acknowledge alert
          </button>
        </div>
      </div>

      <div *ngIf="selectedReconciliationRun as run" class="house-card mt-3">
        <div class="house-card-header">
          <div>
            <h5 class="mb-1">Reconciliation details</h5>
            <p class="text-muted small mb-0">Run {{ run.id }}</p>
          </div>
          <button class="btn-close" type="button" aria-label="Close" (click)="closeReconciliationRun()"></button>
        </div>

        <div class="row g-3 mb-3">
          <div class="col-6 col-md-3"><div class="mini-stat"><span>Status</span><strong>{{ run.status }}</strong></div></div>
          <div class="col-6 col-md-3"><div class="mini-stat"><span>Wallets</span><strong>{{ run.walletCount }}</strong></div></div>
          <div class="col-6 col-md-3"><div class="mini-stat"><span>Pairs</span><strong>{{ run.currencyPairCount }}</strong></div></div>
          <div class="col-6 col-md-3"><div class="mini-stat"><span>Issues</span><strong>{{ run.issueCount }}</strong></div></div>
        </div>

        <div *ngIf="run.failureMessage" class="alert alert-danger">{{ run.failureMessage }}</div>

        <div *ngFor="let issue of run.issues" class="reconciliation-issue" [ngClass]="issueClass(issue.severity)">
          <div class="d-flex justify-content-between gap-2">
            <div>
              <span class="badge me-2" [ngClass]="statusClass(issue.severity)">{{ issue.severity }}</span>
              <strong>{{ issue.code }}</strong>
            </div>
            <small class="text-muted">{{ issue.entityType }} {{ issue.entityId || '' }}</small>
          </div>
          <div class="mt-2">{{ issue.message }}</div>
          <div *ngIf="issue.expectedAmount !== null || issue.actualAmount !== null" class="small text-muted mt-1">
            Expected: {{ issue.expectedAmount ?? '—' }} · Actual: {{ issue.actualAmount ?? '—' }} · Difference: {{ issue.differenceAmount ?? '—' }}
          </div>
        </div>

        <div *ngIf="!run.issues?.length" class="alert alert-success mb-0">No reconciliation issues were detected.</div>
      </div>
    </ng-container>

    <!-- WITHDRAWALS -->
    <ng-container *ngIf="activeTab === 'withdrawals'">
      <div class="mb-3">
        <h5 class="mb-1">House withdrawals</h5>
        <p class="text-muted mb-0">All staff with House Management access can review payout history. Only SuperAdmin can initiate outgoing funds.</p>
      </div>

      <div *ngIf="!canWithdraw" class="alert alert-info">
        Your {{ actorLabel }} role has read-only withdrawal access. SuperAdmin authorization is required to move money out of the house.
      </div>

      <div *ngIf="canWithdraw" class="house-card form-card mb-4">
        <div class="house-card-header">
          <div>
            <h5 class="mb-1">Initiate house withdrawal</h5>
            <p class="text-muted small mb-0">The withdrawal uses the configured payout gateway and your own SuperAdmin PIN policy.</p>
          </div>
          <span class="badge text-bg-danger">SuperAdmin only</span>
        </div>

        <div class="row g-3">
          <div class="col-md-4">
            <label class="form-label">House wallet</label>
            <select class="form-select" [(ngModel)]="withdrawalWalletId" (change)="onWithdrawalWalletChanged()">
              <option value="">Select wallet</option>
              <option *ngFor="let wallet of wallets" [value]="wallet.walletId">
                {{ wallet.currency.code }} — {{ formatAmount(wallet.availableBalance, wallet.currency.symbol) }} available
              </option>
            </select>
          </div>

          <div class="col-md-3">
            <label class="form-label">Payment method override</label>
            <input type="text" class="form-control" [(ngModel)]="withdrawalPaymentMethod" placeholder="Optional">
          </div>

          <div class="col-md-3">
            <label class="form-label">Amount</label>
            <input type="number" min="0" step="0.01" class="form-control" [(ngModel)]="withdrawalAmount">
          </div>

          <div class="col-md-2 d-flex align-items-end">
            <button class="btn btn-outline-primary w-100" type="button" [disabled]="withdrawalConfigLoading || !withdrawalWalletId"
              (click)="loadWithdrawalConfig()">
              <span *ngIf="withdrawalConfigLoading" class="spinner-border spinner-border-sm me-1"></span>
              Load fields
            </button>
          </div>
        </div>

        <div *ngIf="withdrawalConfig" class="destination-panel mt-3">
          <div class="d-flex justify-content-between align-items-center gap-2 mb-3">
            <div>
              <strong>Destination details</strong>
              <div class="small text-muted">
                {{ withdrawalConfig.gateway?.name || 'Configured gateway' }} · {{ withdrawalConfig.paymentMethod || 'Default method' }}
              </div>
            </div>
            <span class="badge text-bg-light border">Minimum {{ formatAmount(withdrawalConfig.minWithdraw, withdrawalConfig.currency.symbol) }}</span>
          </div>

          <div *ngIf="withdrawalConfig.frontendHint" class="alert alert-light border small">
            {{ withdrawalConfig.frontendHint }}
          </div>

          <div class="row g-3">
            <div *ngFor="let field of withdrawalConfig.fields" [ngClass]="field.fieldType === 'textarea' ? 'col-12' : 'col-md-6'">
              <label class="form-label">
                {{ field.label }}
                <span *ngIf="field.isRequired" class="text-danger">*</span>
              </label>

              <select *ngIf="field.fieldType === 'select'" class="form-select"
                [(ngModel)]="withdrawalDestination[field.fieldKey]" [ngModelOptions]="{standalone: true}">
                <option value="">{{ field.placeholder || ('Select ' + field.label) }}</option>
                <option *ngFor="let option of getFieldOptions(field)" [value]="option.value">{{ option.label }}</option>
              </select>

              <textarea *ngIf="field.fieldType === 'textarea'" class="form-control" rows="3"
                [(ngModel)]="withdrawalDestination[field.fieldKey]" [ngModelOptions]="{standalone: true}"
                [placeholder]="field.placeholder || ('Enter ' + field.label)"></textarea>

              <input *ngIf="field.fieldType !== 'select' && field.fieldType !== 'textarea'" class="form-control"
                [type]="field.fieldType === 'number' ? 'number' : (field.fieldType === 'email' ? 'email' : 'text')"
                [(ngModel)]="withdrawalDestination[field.fieldKey]" [ngModelOptions]="{standalone: true}"
                [placeholder]="field.placeholder || ('Enter ' + field.label)">

              <div *ngIf="field.helpText" class="form-text">{{ field.helpText }}</div>
            </div>
          </div>
        </div>

        <div class="d-flex justify-content-end mt-3">
          <button class="btn btn-primary" type="button" [disabled]="withdrawalQuoteLoading || !withdrawalWalletId || !withdrawalAmount"
            (click)="requestWithdrawalQuote()">
            <span *ngIf="withdrawalQuoteLoading" class="spinner-border spinner-border-sm me-1"></span>
            Get withdrawal quote
          </button>
        </div>

        <div *ngIf="withdrawalQuote as quote" class="quote-panel mt-3">
          <div class="row g-3">
            <div class="col-6 col-lg-3"><span>Amount</span><strong>{{ formatAmount(quote.amount) }} {{ quote.currencyCode }}</strong></div>
            <div class="col-6 col-lg-3"><span>Fee</span><strong>{{ formatAmount(quote.fee) }} {{ quote.currencyCode }}</strong></div>
            <div class="col-6 col-lg-3"><span>Total debit</span><strong>{{ formatAmount(quote.totalDebit) }} {{ quote.currencyCode }}</strong></div>
            <div class="col-6 col-lg-3"><span>Recipient gets</span><strong>{{ formatAmount(quote.recipientGets) }} {{ quote.currencyCode }}</strong></div>
          </div>

          <hr>

          <div class="row g-3">
            <div class="col-md-4">
              <label class="form-label">Reason</label>
              <input type="text" class="form-control" [(ngModel)]="withdrawalReason" placeholder="Treasury transfer reason">
            </div>
            <div class="col-md-4">
              <label class="form-label">Idempotency key</label>
              <input type="text" class="form-control" [(ngModel)]="withdrawalIdempotencyKey" placeholder="Generated if empty">
            </div>
            <div class="col-md-4">
              <label class="form-label">Your account PIN</label>
              <input type="password" inputmode="numeric" class="form-control" [(ngModel)]="withdrawalAccountPin" placeholder="Required when PIN protection is enabled">
            </div>
          </div>

          <div class="alert alert-warning mt-3 mb-0">
            This action moves real house funds through the configured gateway. Verify the destination and quote before submitting.
          </div>

          <div class="d-flex justify-content-end gap-2 mt-3">
            <button class="btn btn-light border" type="button" (click)="withdrawalQuote = null">Discard quote</button>
            <button class="btn btn-danger" type="button" [disabled]="withdrawalSubmitting" (click)="initiateWithdrawal()">
              <span *ngIf="withdrawalSubmitting" class="spinner-border spinner-border-sm me-1"></span>
              Initiate house withdrawal
            </button>
          </div>
        </div>
      </div>

      <div class="house-card mb-4">
        <div class="house-card-header">
          <div>
            <h5 class="mb-1">Gateway customer profiles</h5>
            <p class="text-muted small mb-0">Map the house to provider-side customer or wallet identifiers used for operational payouts.</p>
          </div>
          <button class="btn btn-sm btn-outline-primary" type="button" (click)="openGatewayProfile()">Add profile</button>
        </div>

        <div *ngIf="gatewayProfileFormVisible" class="destination-panel mb-3">
          <div class="row g-3">
            <div class="col-md-3">
              <label class="form-label">Provider code</label>
              <input type="text" class="form-control" [(ngModel)]="gatewayProfileProviderCode" [readonly]="!!gatewayProfileForm.externalCustomerId && !!gatewayProfileProviderCode">
            </div>
            <div class="col-md-3">
              <label class="form-label">External customer ID</label>
              <input type="text" class="form-control" [(ngModel)]="gatewayProfileForm.externalCustomerId">
            </div>
            <div class="col-md-3">
              <label class="form-label">External wallet ID</label>
              <input type="text" class="form-control" [(ngModel)]="gatewayProfileForm.externalWalletId">
            </div>
            <div class="col-md-3">
              <label class="form-label">Verification status</label>
              <input type="text" class="form-control" [(ngModel)]="gatewayProfileForm.verificationStatus">
            </div>
            <div class="col-12">
              <label class="form-label">Metadata JSON</label>
              <textarea class="form-control font-monospace" rows="3" [(ngModel)]="gatewayProfileForm.metadataJson" placeholder='{"environment":"live"}'></textarea>
            </div>
          </div>
          <div class="d-flex justify-content-end gap-2 mt-3">
            <button class="btn btn-light border" (click)="closeGatewayProfile()">Cancel</button>
            <button class="btn btn-primary" [disabled]="savingProfile" (click)="saveGatewayProfile()">
              <span *ngIf="savingProfile" class="spinner-border spinner-border-sm me-1"></span>
              Save profile
            </button>
          </div>
        </div>

        <div *ngIf="gatewayProfilesLoading" class="house-loading"><div class="spinner-border text-primary"></div></div>
        <div class="table-responsive" *ngIf="!gatewayProfilesLoading">
          <table class="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th>Provider</th>
                <th>Customer ID</th>
                <th>Wallet ID</th>
                <th>Status</th>
                <th>Updated</th>
                <th class="text-end">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let profile of gatewayProfiles">
                <td class="fw-semibold">{{ profile.providerCode }}</td>
                <td>{{ profile.externalCustomerId }}</td>
                <td>{{ profile.externalWalletId || '—' }}</td>
                <td><span class="badge" [ngClass]="statusClass(profile.verificationStatus)">{{ profile.verificationStatus }}</span></td>
                <td>{{ formatDate(profile.lastUpdatedAt) }}</td>
                <td class="text-end"><button class="btn btn-sm btn-outline-secondary" (click)="openGatewayProfile(profile)">Edit</button></td>
              </tr>
              <tr *ngIf="!gatewayProfiles.length">
                <td colspan="6" class="text-center text-muted py-4">No house gateway profiles found.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="house-card filter-card mb-3">
        <div class="row g-3 align-items-end">
          <div class="col-md-4">
            <label class="form-label">Withdrawal status</label>
            <select class="form-select" [(ngModel)]="withdrawalFilters.status">
              <option value="">All statuses</option>
              <option value="Pending">Pending</option>
              <option value="Processing">Processing</option>
              <option value="Confirmed">Confirmed</option>
              <option value="Completed">Completed</option>
              <option value="Failed">Failed</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>
          <div class="col-md-4">
            <label class="form-label">Currency</label>
            <input type="text" maxlength="3" class="form-control" [(ngModel)]="withdrawalFilters.currency" placeholder="CAD">
          </div>
          <div class="col-md-4 d-flex gap-2">
            <button class="btn btn-primary flex-grow-1" (click)="loadWithdrawals(1)">Apply</button>
            <button class="btn btn-light border" (click)="resetWithdrawalFilters()">Reset</button>
          </div>
        </div>
      </div>

      <div class="house-card">
        <div class="house-card-header">
          <div>
            <h5 class="mb-1">Withdrawal history</h5>
            <p class="text-muted small mb-0">Operational payouts initiated from the house account.</p>
          </div>
        </div>

        <div *ngIf="withdrawalsLoading" class="house-loading"><div class="spinner-border text-primary"></div></div>
        <div class="table-responsive" *ngIf="!withdrawalsLoading">
          <table class="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th>Created</th>
                <th>Reference</th>
                <th>Currency</th>
                <th class="text-end">Amount</th>
                <th class="text-end">Fee</th>
                <th>Method</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let withdrawal of withdrawals">
                <td>{{ formatDate(withdrawal.createdAt) }}</td>
                <td><code>{{ withdrawal.externalReference }}</code></td>
                <td>{{ withdrawal.currency.code }}</td>
                <td class="text-end">{{ formatAmount(withdrawal.amount, withdrawal.currency.symbol) }}</td>
                <td class="text-end">{{ formatAmount(withdrawal.feeAmount, withdrawal.currency.symbol) }}</td>
                <td>{{ withdrawal.paymentMethod || '—' }}</td>
                <td>
                  <span class="badge" [ngClass]="statusClass(withdrawal.status)">{{ withdrawal.status }}</span>
                  <div *ngIf="withdrawal.failureReason" class="text-danger small mt-1">{{ withdrawal.failureReason }}</div>
                </td>
              </tr>
              <tr *ngIf="!withdrawals.length">
                <td colspan="7" class="text-center text-muted py-4">No house withdrawals found.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="table-pager" *ngIf="withdrawalsMeta">
          <span>Page {{ withdrawalsMeta.currentPage }} of {{ withdrawalsMeta.totalPages || 1 }}</span>
          <div class="btn-group btn-group-sm">
            <button class="btn btn-outline-secondary" [disabled]="withdrawalsMeta.currentPage <= 1"
              (click)="loadWithdrawals(withdrawalsMeta.currentPage - 1)">Previous</button>
            <button class="btn btn-outline-secondary" [disabled]="withdrawalsMeta.currentPage >= withdrawalsMeta.totalPages"
              (click)="loadWithdrawals(withdrawalsMeta.currentPage + 1)">Next</button>
          </div>
        </div>
      </div>
    </ng-container>

    <app-footer></app-footer>
  </main>
</div>
